import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { History, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { dealService, type Deal } from '@/services/deal.service';
import { cn } from '@/lib/utils';

export function DealHistoryPage() {
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [sortField, setSortField] = useState<'createdAt' | 'claims' | 'revenue'>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data: deals = [], isLoading } = useQuery({
    queryKey: ['deal-history'],
    queryFn: dealService.getDealHistory,
  });

  const sorted = [...deals].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortField === 'createdAt') {
      return dir * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }
    if (sortField === 'claims') {
      return dir * ((a.metrics?.claims || 0) - (b.metrics?.claims || 0));
    }
    if (sortField === 'revenue') {
      return dir * ((a.metrics?.revenueGenerated || 0) - (b.metrics?.revenueGenerated || 0));
    }
    return 0;
  });

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? (
      <ChevronUp className="h-3.5 w-3.5" />
    ) : (
      <ChevronDown className="h-3.5 w-3.5" />
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      completed: 'bg-green-100 text-green-700',
      expired: 'bg-gray-100 text-gray-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    return (
      <span
        className={cn(
          'rounded-full px-2 py-0.5 text-xs font-medium capitalize',
          styles[status] || 'bg-gray-100 text-gray-700'
        )}
      >
        {status}
      </span>
    );
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <History className="h-8 w-8" />
          Deal History
        </h1>
        <p className="mt-1 text-muted-foreground">Past deals that have completed, expired, or been cancelled</p>
      </div>

      {deals.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-lg text-muted-foreground">No deal history yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Once your deals complete or expire, they will appear here
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                      Item
                    </th>
                    <th
                      className="cursor-pointer px-4 py-3 text-left text-sm font-medium text-gray-600"
                      onClick={() => toggleSort('claims')}
                    >
                      <span className="flex items-center gap-1">
                        Total Claims <SortIcon field="claims" />
                      </span>
                    </th>
                    <th
                      className="cursor-pointer px-4 py-3 text-left text-sm font-medium text-gray-600"
                      onClick={() => toggleSort('revenue')}
                    >
                      <span className="flex items-center gap-1">
                        Revenue <SortIcon field="revenue" />
                      </span>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                      Avg Arrival
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                      Status
                    </th>
                    <th
                      className="cursor-pointer px-4 py-3 text-left text-sm font-medium text-gray-600"
                      onClick={() => toggleSort('createdAt')}
                    >
                      <span className="flex items-center gap-1">
                        Date <SortIcon field="createdAt" />
                      </span>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sorted.map((deal) => (
                    <tr key={deal.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {deal.item.photoUrl && (
                            <img
                              src={deal.item.photoUrl}
                              alt={deal.item.name}
                              className="h-10 w-10 rounded object-cover"
                            />
                          )}
                          <div>
                            <p className="font-medium">{deal.item.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {deal.item.originalPrice} {deal.item.currency}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {deal.metrics?.claims || 0} / {deal.slotsTotal}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {(deal.metrics?.revenueGenerated || 0).toFixed(2)} AZN
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {deal.metrics?.avgArrivalSeconds
                          ? `${Math.round(deal.metrics.avgArrivalSeconds / 60)} min`
                          : '--'}
                      </td>
                      <td className="px-4 py-3">{statusBadge(deal.status)}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {format(new Date(deal.createdAt), 'MMM d, yyyy')}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedDeal(deal)}
                        >
                          Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail Modal */}
      {selectedDeal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="mx-4 w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">{selectedDeal.item.name}</CardTitle>
              <button onClick={() => setSelectedDeal(null)}>
                <X className="h-5 w-5" />
              </button>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedDeal.item.photoUrl && (
                <img
                  src={selectedDeal.item.photoUrl}
                  alt={selectedDeal.item.name}
                  className="h-48 w-full rounded-lg object-cover"
                />
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Original Price</p>
                  <p className="font-semibold">
                    {selectedDeal.item.originalPrice} {selectedDeal.item.currency}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <p>{statusBadge(selectedDeal.status)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Claims</p>
                  <p className="font-semibold">
                    {selectedDeal.metrics?.claims || 0} / {selectedDeal.slotsTotal}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Revenue Generated</p>
                  <p className="font-semibold">
                    {(selectedDeal.metrics?.revenueGenerated || 0).toFixed(2)} AZN
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Notifications Sent</p>
                  <p className="font-semibold">{selectedDeal.metrics?.notificationsSent || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Avg Arrival Time</p>
                  <p className="font-semibold">
                    {selectedDeal.metrics?.avgArrivalSeconds
                      ? `${Math.round(selectedDeal.metrics.avgArrivalSeconds / 60)} min`
                      : '--'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Rushes Started</p>
                  <p className="font-semibold">{selectedDeal.metrics?.rushesStarted || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Rushes Completed</p>
                  <p className="font-semibold">{selectedDeal.metrics?.rushesCompleted || 0}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground">Created</p>
                <p className="text-sm">
                  {format(new Date(selectedDeal.createdAt), 'PPpp')}
                </p>
              </div>

              <Button variant="outline" className="w-full" onClick={() => setSelectedDeal(null)}>
                Close
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
